/**
 * ranking-page-custom.js
 *
 * - Category switch → <a href> page navigation (PC sidebar)
 *                    → <select> lv1 navigation (SP dropdown)
 *                    → <a> lv2 navigation (SP horizontal tabs)
 * - Period switch → DOM re-sort by data-rank-{period} + force badge text update
 * - Layout switch → <layout-switcher> from facets.js
 */
(function () {
  'use strict';

  var SEL = {
    grid: '#ProductsList',
    periodTabs: '#RankingPeriodTabs',
    periodBtn: '.ranking-period-tabs__btn',
    productColumn: '.ranking-product-column',
    rankingBadge: '.pcard-ranking-badge',
    mobileSelect: '#RankingCategorySelect',
    mobileTabsScroll: '.ranking-mobile-nav__tabs-scroll',
    mobileTabActive: '.ranking-mobile-nav__tab.is-active',
  };

  var currentPeriod = 'monthly';

  /* =========================================================
     URL parameter helpers — sync ?period=monthly|yearly
     ========================================================= */
  function getPeriodFromURL() {
    try {
      var p = new URL(window.location.href).searchParams.get('period');
      return (p === 'yearly' || p === 'monthly') ? p : null;
    } catch (e) {
      return null;
    }
  }

  function updateURLParam(period, replace) {
    try {
      var url = new URL(window.location.href);
      if (period === 'monthly') {
        /* monthly is default — remove the param to keep URL clean */
        url.searchParams.delete('period');
      } else {
        url.searchParams.set('period', period);
      }
      var newUrl = url.pathname + (url.search ? url.search : '') + url.hash;
      if (replace) {
        window.history.replaceState({ period: period }, '', newUrl);
      } else {
        window.history.pushState({ period: period }, '', newUrl);
      }
    } catch (e) {
      /* no-op if History API fails */
    }
  }

  function setActiveTabUI(period) {
    var tabs = document.querySelector(SEL.periodTabs);
    if (!tabs) return;
    tabs.querySelectorAll(SEL.periodBtn).forEach(function (b) {
      if (b.dataset.period === period) {
        b.classList.add('ranking-period-tabs__btn--active');
      } else {
        b.classList.remove('ranking-period-tabs__btn--active');
      }
    });
  }

  /* =========================================================
     Mobile lv1 select → page navigation
     ========================================================= */
  function initMobileCategory() {
    var select = document.querySelector(SEL.mobileSelect);
    if (!select) return;
    select.addEventListener('change', function () {
      if (select.value) window.location.href = select.value;
    });
  }

  /* =========================================================
     Mobile lv2 tabs → auto-scroll active tab into view
     ========================================================= */
  function initMobileTabs() {
    var scroll = document.querySelector(SEL.mobileTabsScroll);
    var active = document.querySelector(SEL.mobileTabActive);
    if (!scroll || !active) return;
    var scrollLeft = active.offsetLeft - (scroll.clientWidth / 2) + (active.offsetWidth / 2);
    scroll.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
  }

  /* =========================================================
     Force badge text/data-rank to a specific number.
     Creates the badge element if it doesn't exist (some products
     don't render .pcard-ranking-badge in card-product snippet).
     ========================================================= */
  function setBadge(col, rank) {
    var badge = col.querySelector(SEL.rankingBadge);

    if (!badge) {
      /* Create new badge. Insertion target: inside motion-element if found,
         otherwise inside .product-card__image-wrapper, else fallback to col root. */
      badge = document.createElement('span');
      badge.className = 'pcard-ranking-badge';

      var insertTarget =
        col.querySelector('motion-element') ||
        col.querySelector('.product-card__image-wrapper') ||
        col;
      insertTarget.appendChild(badge);
    }

    badge.textContent = String(rank);
    badge.setAttribute('data-rank', String(rank));
  }

  /* =========================================================
     Period tabs — click handler
     ========================================================= */
  function initPeriodTabs() {
    var tabs = document.querySelector(SEL.periodTabs);
    if (!tabs) return;

    /* Determine initial period: URL param > grid data-period > default monthly */
    var grid = document.querySelector(SEL.grid);
    var initialPeriod =
      getPeriodFromURL() ||
      (grid && grid.dataset.period) ||
      'monthly';
    if (initialPeriod !== 'yearly' && initialPeriod !== 'monthly') {
      initialPeriod = 'monthly';
    }
    currentPeriod = initialPeriod;

    /* Sync UI + DOM to initial period (no history push on first run) */
    setActiveTabUI(initialPeriod);
    applyPeriod(initialPeriod);
    /* Replace state so back button always returns to current view */
    updateURLParam(initialPeriod, true);

    tabs.addEventListener('click', function (e) {
      var btn = e.target.closest(SEL.periodBtn);
      if (!btn) return;

      var period = btn.dataset.period;
      if (!period) return;
      if (period === currentPeriod) return;

      currentPeriod = period;
      setActiveTabUI(period);
      applyPeriod(period);
      updateURLParam(period, false);
    });

    /* Browser back/forward: re-sync to URL state */
    window.addEventListener('popstate', function () {
      var p = getPeriodFromURL() || 'monthly';
      if (p === currentPeriod) return;
      currentPeriod = p;
      setActiveTabUI(p);
      applyPeriod(p);
    });
  }

  /* =========================================================
     Apply period — sort by data-rank-{period}, show top-N, hide rest,
     force-rewrite badge numbers as 1..N regardless of prior content.
     ========================================================= */
  function applyPeriod(period) {
    var grid = document.querySelector(SEL.grid);
    if (!grid) return;

    var cols = Array.from(grid.querySelectorAll(SEL.productColumn));
    if (!cols.length) return;

    var perPage = parseInt(grid.dataset.productsPerPage, 10) || 10;
    var rankAttr = period === 'yearly' ? 'rankYearly' : 'rankMonthly';

    /* Partition: cards with rank > 0 in current period vs. without */
    var ranked = [];
    var unranked = [];
    cols.forEach(function (col) {
      var r = parseInt(col.dataset[rankAttr], 10) || 0;
      if (r > 0) {
        ranked.push(col);
      } else {
        unranked.push(col);
      }
    });

    /* Sort ranked ascending by their period rank (1, 2, 3, ...) */
    ranked.sort(function (a, b) {
      return (parseInt(a.dataset[rankAttr], 10) || 0) - (parseInt(b.dataset[rankAttr], 10) || 0);
    });

    /* Slice to top perPage; the rest go to hidden bucket */
    var visible = ranked.slice(0, perPage);
    var hidden = ranked.slice(perPage).concat(unranked);

    /* Rebuild DOM in single pass: visible (1..N) + hidden */
    var frag = document.createDocumentFragment();

    visible.forEach(function (col, i) {
      var displayRank = i + 1;
      col.classList.remove('is-hidden-rank');
      col.removeAttribute('hidden');
      col.style.removeProperty('display');
      setBadge(col, displayRank);
      frag.appendChild(col);
    });

    hidden.forEach(function (col) {
      col.classList.add('is-hidden-rank');
      frag.appendChild(col);
    });

    grid.appendChild(frag);
    grid.dataset.period = period;
  }

  /* =========================================================
     Init — runs at multiple timings to defend against
     race conditions with card-product snippet rendering.
     ========================================================= */
  function init() {
    initMobileCategory();
    initMobileTabs();
    initPeriodTabs();
  }

  function bootstrap() {
    init();
    /* Re-apply current period after all sync rendering settles
       (covers case where card-product/quick-view scripts
       overwrite badge content slightly later) */
    setTimeout(function () {
      applyPeriod(currentPeriod);
    }, 50);
    setTimeout(function () {
      applyPeriod(currentPeriod);
    }, 300);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
  /* Also re-run on full window load for max safety */
  window.addEventListener('load', function () {
    setTimeout(function () {
      applyPeriod(currentPeriod);
    }, 0);
  });
})();

